import { useEffect, useState } from "react"

interface CTAVisibilityState {
  isBracketSectionInView: boolean
  isExpandedCardHalfVisible: boolean
  isUserRested: boolean
  allowBracketInteractions: boolean
  isDemoSectionInView: boolean
  demoInteracted: boolean
}

export function useCTAVisibility(variant: "inline" | "tall-screen" | "mobile"): CTAVisibilityState {
  const [isBracketSectionInView, setIsBracketSectionInView] = useState(false)
  const [isExpandedCardHalfVisible, setIsExpandedCardHalfVisible] = useState(false)
  const [isUserRested, setIsUserRested] = useState(true)
  const [allowBracketInteractions, setAllowBracketInteractions] = useState(true)
  const [isDemoSectionInView, setIsDemoSectionInView] = useState(false)
  const [demoInteracted, setDemoInteracted] = useState(false)

  // Mobile-specific: Observe demo section visibility
  useEffect(() => {
    if (variant !== "mobile") return
    if (typeof window === "undefined") return

    const demoSection = document.getElementById("demo-brackets")
    if (!demoSection) return

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        setIsDemoSectionInView(entry.isIntersecting)
      },
      { threshold: 0.1 },
    )

    io.observe(demoSection)
    return () => io.disconnect()
  }, [variant])

  // Mobile-specific: Track demo interaction (when user expands a card)
  useEffect(() => {
    if (variant !== "mobile") return

    const handleCardExpanded = () => {
      if (isDemoSectionInView) {
        setDemoInteracted(true)
      }
    }

    window.addEventListener("bracketCardExpanded", handleCardExpanded)
    return () => window.removeEventListener("bracketCardExpanded", handleCardExpanded)
  }, [variant, isDemoSectionInView])

  // Mobile-specific: Reset interaction flag when user leaves demo section
  useEffect(() => {
    if (variant !== "mobile") return
    if (!isDemoSectionInView) {
      setDemoInteracted(false)
    }
  }, [variant, isDemoSectionInView])

  // Desktop inline: Listen for navigation intent changes
  useEffect(() => {
    if (variant !== "inline") return

    const handleCloseAllBracketCards = () => {
      setAllowBracketInteractions(false)
    }

    const handleBracketNavClicked = () => {
      setAllowBracketInteractions(true)
    }

    window.addEventListener("closeAllBracketCards", handleCloseAllBracketCards)
    window.addEventListener("bracketNavClicked", handleBracketNavClicked)

    return () => {
      window.removeEventListener("closeAllBracketCards", handleCloseAllBracketCards)
      window.removeEventListener("bracketNavClicked", handleBracketNavClicked)
    }
  }, [variant])

  // Desktop inline: Consolidated visibility and interaction tracking
  useEffect(() => {
    if (variant !== "inline") return
    if (typeof window === "undefined") return
    if (window.location.pathname !== "/") return

    const bracketSection = document.getElementById("bracket")
    if (!bracketSection) return

    let scrollTimeout: NodeJS.Timeout
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsUserRested(false)

          clearTimeout(scrollTimeout)
          scrollTimeout = setTimeout(() => {
            setIsUserRested(true)
          }, 150)

          ticking = false
        })
        ticking = true
      }
    }

    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const isVisible = entry.isIntersecting
        setIsBracketSectionInView(isVisible)

        if (isVisible) {
          const navClicked = sessionStorage.getItem("bracket:nav-clicked")
          if (!navClicked) {
            setAllowBracketInteractions(true)
          }
        }
      },
      { threshold: [0.1, 0.3] },
    )

    let cardObserver: IntersectionObserver | null = null
    let currentObserved: Element | null = null

    const observeCard = () => {
      const card = document.querySelector(".bracket-card.expanded")
      if (!card || card === currentObserved) return

      if (cardObserver && currentObserved) {
        cardObserver.unobserve(currentObserved)
      }

      cardObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            setIsExpandedCardHalfVisible(entry.intersectionRatio >= 0.5)
          }
        },
        { threshold: [0, 0.5, 1] },
      )
      cardObserver.observe(card)
      currentObserved = card
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    sectionObserver.observe(bracketSection)
    observeCard()

    const mutObs = new MutationObserver(observeCard)
    mutObs.observe(document.body, { attributes: true, childList: true, subtree: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      clearTimeout(scrollTimeout)
      sectionObserver.disconnect()
      cardObserver?.disconnect()
      mutObs.disconnect()
    }
  }, [variant])

  return {
    isBracketSectionInView,
    isExpandedCardHalfVisible,
    isUserRested,
    allowBracketInteractions,
    isDemoSectionInView,
    demoInteracted,
  }
}
